import { useQuery, useQueryClient } from "@tanstack/react-query";
import { eachDayOfInterval } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Plus,
  Printer,
  RotateCcw,
  Save,
  SlidersHorizontal,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, jsonBody, queryString } from "../../api/client";
import type { ListResponse } from "../../api/types";
import { useVenueScope } from "../../app/venueScope";
import { useWorkspace } from "../../app/workspace";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows, membershipAllowsIn } from "../../auth/permissions";
import { titleCase } from "../../lib/format";
import { useMediaQuery } from "../../lib/useMediaQuery";
import {
  Card,
  CardHeader,
  EmptyState,
  ErrorPanel,
  Field,
  Notice,
  PageHeader,
} from "../../ui/components";
import { CalendarSkeleton } from "../../ui/skeletons";
import { EVENT_STATUSES } from "../events/eventLifecycle";
import { useAllLocations } from "../venues/queries";
import { CalendarViews } from "./CalendarViews";
import {
  type CalendarFilters,
  type CalendarMode,
  type CalendarResponse,
  type CalendarView,
  type CalendarViewDefinition,
  type CalendarViewList,
  emptyCalendarFilters,
} from "./calendarTypes";
import {
  calendarCSV,
  calendarExportFilename,
  calendarRange,
  calendarViewTitle,
  moveCalendarAnchor,
} from "./calendarView";

interface CalendarMember {
  userId: string;
  displayName: string;
  email: string;
  membershipStatus: string;
}

// The states a hire moves through, which describe negotiating with somebody else over the space.
// The client's own programme never negotiates with anybody, so it has its own list in
// EVENT_STATUSES and neither side is offered the other's vocabulary.
const BOOKING_STATUSES = [
  "draft",
  "enquiry",
  "held",
  "provisional",
  "pending_approval",
  "confirmed",
  "in_progress",
  "completed",
  "cancelled",
];

// What each side can be narrowed to when looking for trouble. Both sides file tasks and hold
// equipment now, so the two lists differ only in what the calendar can actually read for each:
// mirrors calendarAttention in the diary repository.
const BOOKING_ATTENTION = [
  { value: "conflicts", label: "Conflict overrides" },
  { value: "tasks", label: "Open tasks" },
  { value: "overdue_tasks", label: "Overdue tasks" },
  { value: "payments", label: "Payment attention" },
  { value: "inventory", label: "Inventory assigned" },
  { value: "sponsors", label: "Sponsor attached" },
];

const EVENT_ATTENTION = [
  { value: "conflicts", label: "Conflict overrides" },
  { value: "tasks", label: "Open tasks" },
  { value: "overdue_tasks", label: "Overdue tasks" },
  { value: "payments", label: "Payment attention" },
  { value: "inventory", label: "Inventory assigned" },
  { value: "sponsors", label: "Sponsor attached" },
];

export function CalendarPage() {
  const auth = useAuth();
  const { workspace } = useWorkspace();
  const { selectedVenueId, venues, loading: venuesLoading } = useVenueScope();
  const queryClient = useQueryClient();
  const defaultApplied = useRef(false);
  // Today rather than the first of the month: the month view finds its own first day from any
  // date, and the week and day views open on the week and day that today is in, which is what a
  // phone opening in week view needs.
  const [anchor, setAnchor] = useState(() => new Date());
  const [chosenMode, setMode] = useState<CalendarMode>("month");
  // Below tablet width a month grid is a small window onto a large table, so Month is withheld
  // there and reads as Week; the choice itself is kept, so a phone turned into a tablet, or a saved
  // view that names the month, gets the month back the moment there is room for it.
  const compact = useMediaQuery("(max-width: 56rem)");
  const mode: CalendarMode = compact && chosenMode === "month" ? "week" : chosenMode;
  const [filters, setFilters] = useState<CalendarFilters>({ ...emptyCalendarFilters });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedViewId, setSelectedViewId] = useState("");
  const [viewName, setViewName] = useState("");
  const [viewVisibility, setViewVisibility] = useState<"private" | "tenant">("private");
  const [viewDefault, setViewDefault] = useState(false);
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState<unknown>();
  const [success, setSuccess] = useState("");
  const range = useMemo(() => calendarRange(mode, anchor), [mode, anchor]);
  const showingEvents = workspace === "events";
  const noun = showingEvents ? "event" : "booking";
  const attentionOptions = showingEvents ? EVENT_ATTENTION : BOOKING_ATTENTION;
  // Narrowings that only describe a hire are dropped rather than sent on the event side. An event
  // has no approval step and arrives through no channel, so asking for approved events returns
  // nothing at all, and an empty calendar reads as a fault rather than as a filter.
  const appliedFilters: CalendarFilters = showingEvents
    ? {
        ...filters,
        approval: "",
        source: "",
        attention: attentionOptions.some((option) => option.value === filters.attention)
          ? filters.attention
          : "",
      }
    : filters;
  // What the Filters button owes somebody who has not opened it: how many narrowings are
  // currently shaping the grid they are looking at.
  const activeFilterCount = Object.values(appliedFilters).filter((value) => value !== "").length;
  const query = queryString({
    from: range.from.toISOString(),
    to: range.to.toISOString(),
    ...appliedFilters,
    // The venue question is answered once, by the switch in the top bar. It is written after
    // the spread so a saved view from the all-venues era can never widen the scope.
    venueId: selectedVenueId,
    // One endpoint, narrowed to the side being looked at. Answering with both halves would put
    // third-party hires in front of somebody who came to see their own programme, which is the
    // question the event workspace exists to ask on its own.
    kind: showingEvents ? "event" : "booking",
  });
  const calendar = useQuery({
    queryKey: ["calendar", mode, query],
    queryFn: () => api<CalendarResponse>(`/api/v1/calendar${query}`),
    enabled: Boolean(selectedVenueId),
  });
  const savedViews = useQuery({
    queryKey: ["calendar", "views"],
    queryFn: () => api<CalendarViewList>("/api/v1/calendar/views"),
  });
  // Only the scoped venue's spaces belong in the location filter; the page never mixes venues.
  const scopedVenues = useMemo(
    () => venues.filter((venue) => venue.id === selectedVenueId),
    [venues, selectedVenueId],
  );
  const locationsQuery = useAllLocations(scopedVenues);
  const members = useQuery({
    queryKey: ["tenant", "members", "calendar-filter"],
    queryFn: () => api<ListResponse<CalendarMember>>("/api/v1/members"),
  });
  const days = useMemo(
    () => eachDayOfInterval({ start: range.from, end: range.to }),
    [range.from, range.to],
  );
  const selectedView = savedViews.data?.items.find((view) => view.id === selectedViewId);
  const membership = auth.session?.selectedMembership;
  // Creating and rescheduling are one-sided acts, so they are asked of one side. Sharing a saved
  // view is not: the server checks the union for tenant visibility, because a view is a way of
  // looking at the diary rather than something either trade owns.
  const canManageBookings = membershipAllowsIn(membership, "bookings", "bookings.manage");
  const canCreate = showingEvents
    ? membershipAllowsIn(membership, "events", "events.manage")
    : canManageBookings;
  const createTarget = showingEvents ? "/events/new" : "/calendar/new";
  const createPath = canCreate ? createTarget : "";
  const canShareViews = membershipAllows(membership, "bookings.manage");
  const canEditSelectedView = selectedView?.createdBy === auth.session?.user.id;

  const applyView = useCallback((view: CalendarView) => {
    const definition = view.definition as CalendarViewDefinition;
    setMode(
      ["day", "week", "month", "agenda", "portfolio"].includes(definition.mode)
        ? definition.mode
        : "month",
    );
    setFilters({ ...emptyCalendarFilters, ...(definition.filters ?? {}) });
    setSelectedViewId(view.id);
    setViewName(view.name);
    setViewVisibility(view.visibility);
    setViewDefault(view.isDefault);
  }, []);

  useEffect(() => {
    if (defaultApplied.current || !savedViews.data) return;
    defaultApplied.current = true;
    const defaultView = savedViews.data.items.find((view) => view.isDefault);
    if (defaultView) applyView(defaultView);
  }, [savedViews.data, applyView]);

  function setFilter<Key extends keyof CalendarFilters>(key: Key, value: CalendarFilters[Key]) {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  function resetFilters() {
    setFilters({ ...emptyCalendarFilters });
    setSelectedViewId("");
    setViewName("");
    setViewDefault(false);
  }

  function move(direction: -1 | 1) {
    setAnchor((current) => moveCalendarAnchor(mode, current, direction));
  }

  function viewPayload(version = 0) {
    return {
      name: viewName.trim(),
      definition: { mode, filters },
      visibility: viewVisibility,
      isDefault: viewDefault,
      version,
    };
  }

  async function saveView(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!viewName.trim()) return;
    setWorking(true);
    setActionError(undefined);
    setSuccess("");
    try {
      const created = await api<CalendarView>("/api/v1/calendar/views", {
        method: "POST",
        body: jsonBody(viewPayload()),
      });
      await queryClient.invalidateQueries({ queryKey: ["calendar", "views"] });
      setSelectedViewId(created.id);
      setSuccess("Calendar view saved.");
    } catch (caught) {
      setActionError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function updateView() {
    if (!selectedView || !canEditSelectedView || !viewName.trim()) return;
    setWorking(true);
    setActionError(undefined);
    setSuccess("");
    try {
      const updated = await api<CalendarView>(`/api/v1/calendar/views/${selectedView.id}`, {
        method: "PUT",
        body: jsonBody(viewPayload(selectedView.version)),
      });
      await queryClient.invalidateQueries({ queryKey: ["calendar", "views"] });
      setSelectedViewId(updated.id);
      setSuccess("Calendar view updated.");
    } catch (caught) {
      setActionError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function deleteView() {
    if (!selectedView || !canEditSelectedView) return;
    setWorking(true);
    setActionError(undefined);
    setSuccess("");
    try {
      await api(`/api/v1/calendar/views/${selectedView.id}`, { method: "DELETE" });
      await queryClient.invalidateQueries({ queryKey: ["calendar", "views"] });
      resetFilters();
      setSuccess("Calendar view deleted.");
    } catch (caught) {
      setActionError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (!venuesLoading && venues.length === 0) {
    return (
      <>
        <PageHeader
          title="Calendar"
          description="The calendar lives inside a venue, and this organization does not have one yet."
        />
        <EmptyState
          title="No venues yet"
          description="Create a venue to start planning its calendar."
        />
      </>
    );
  }
  if (calendar.isPending || venuesLoading || savedViews.isLoading) {
    return <CalendarSkeleton />;
  }
  if (calendar.error || locationsQuery.error || savedViews.error) {
    return <ErrorPanel error={calendar.error ?? locationsQuery.error ?? savedViews.error} />;
  }

  const items = calendar.data?.items ?? [];

  function exportCalendar() {
    const blob = new Blob([calendarCSV(items)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = calendarExportFilename(range.from, range.to);
    link.click();
    URL.revokeObjectURL(url);
    setSuccess("Calendar CSV downloaded.");
  }

  return (
    <div className="calendar-page">
      <PageHeader
        title="Calendar"
        description={
          showingEvents
            ? "The program this organization is running, with the spaces it holds, the invoices issued against it, and the sponsors attached."
            : "Bookings, conflicts, payments, tasks, inventory, and sponsors for the venue you are viewing."
        }
        actions={
          <div className="page-actions no-print">
            <button className="button secondary" type="button" onClick={() => window.print()}>
              <Printer size={17} /> Print
            </button>
            <button className="button secondary" type="button" onClick={exportCalendar}>
              <Download size={17} /> Export CSV
            </button>
            {createPath ? (
              <Link className="button primary" to={createPath}>
                <Plus size={17} /> New {noun}
              </Link>
            ) : null}
          </div>
        }
      />
      {actionError ? <ErrorPanel error={actionError} /> : null}
      {success ? <Notice tone="success">{success}</Notice> : null}

      <Card className="calendar-controls no-print">
        <div className="calendar-toolbar">
          <div className="inline">
            <button
              className="icon-button"
              type="button"
              aria-label="Previous period"
              onClick={() => move(-1)}
            >
              <ChevronLeft />
            </button>
            <button
              className="button secondary"
              type="button"
              onClick={() => setAnchor(new Date())}
            >
              Today
            </button>
            <button
              className="icon-button"
              type="button"
              aria-label="Next period"
              onClick={() => move(1)}
            >
              <ChevronRight />
            </button>
            <h2>{calendarViewTitle(mode, anchor, range.from, range.to)}</h2>
          </div>
          <div className="inline">
            <fieldset className="segmented-control" aria-label="Calendar view">
              {(["day", "week", "month", "agenda", "portfolio"] as CalendarMode[])
                .filter((value) => !compact || value !== "month")
                .map((value) => (
                  <button
                    key={value}
                    className={mode === value ? "active" : ""}
                    type="button"
                    aria-pressed={mode === value}
                    onClick={() => setMode(value)}
                  >
                    {value === "portfolio" ? "Spaces" : titleCase(value)}
                  </button>
                ))}
            </fieldset>
            <button
              className="button secondary"
              type="button"
              aria-expanded={filtersOpen}
              onClick={() => setFiltersOpen((open) => !open)}
            >
              <SlidersHorizontal size={16} /> Filters
              {activeFilterCount > 0 ? (
                <span className="filter-count">{activeFilterCount}</span>
              ) : null}
            </button>
          </div>
        </div>
      </Card>

      {filtersOpen ? (
        <Card className="calendar-controls no-print">
          <CardHeader
            title="Filters and saved views"
            description="Combine operational filters, then save a private or tenant-wide view."
          />
          <div className="filter-grid">
            <Field label="Saved view">
              <select
                value={selectedViewId}
                onChange={(event) => {
                  const view = savedViews.data?.items.find(
                    (item) => item.id === event.target.value,
                  );
                  if (view) applyView(view);
                  else resetFilters();
                }}
              >
                <option value="">Unsaved view</option>
                {savedViews.data?.items.map((view) => (
                  <option key={view.id} value={view.id}>
                    {view.name}
                    {view.visibility === "tenant" ? " · Shared" : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Location">
              <select
                value={filters.locationId}
                onChange={(event) => setFilter("locationId", event.target.value)}
              >
                <option value="">All locations</option>
                {locationsQuery.locations.map((location) => (
                  <option value={location.id} key={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={showingEvents ? "Event status" : "Booking status"}>
              <select
                value={filters.status}
                onChange={(event) => setFilter("status", event.target.value)}
              >
                <option value="">All statuses</option>
                {(showingEvents ? EVENT_STATUSES : BOOKING_STATUSES).map((value) => (
                  <option key={value} value={value}>
                    {titleCase(value)}
                  </option>
                ))}
              </select>
            </Field>
            {showingEvents ? null : (
              <>
                <Field label="Approval">
                  <select
                    value={filters.approval}
                    onChange={(event) => setFilter("approval", event.target.value)}
                  >
                    <option value="">All approval states</option>
                    {["not_required", "pending", "approved", "rejected"].map((value) => (
                      <option key={value} value={value}>
                        {titleCase(value)}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Source">
                  <select
                    value={filters.source}
                    onChange={(event) => setFilter("source", event.target.value)}
                  >
                    <option value="">All sources</option>
                    <option value="staff">Staff</option>
                    <option value="public">Public booking</option>
                    <option value="portal">Customer portal</option>
                  </select>
                </Field>
              </>
            )}
            <Field label="Owner">
              <select
                value={filters.ownerId}
                onChange={(event) => setFilter("ownerId", event.target.value)}
              >
                <option value="">All owners</option>
                {members.data?.items
                  .filter((member) => member.membershipStatus === "active")
                  .map((member) => (
                    <option key={member.userId} value={member.userId}>
                      {member.displayName || member.email}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Customer">
              <input
                type="search"
                value={filters.customer}
                onChange={(event) => setFilter("customer", event.target.value)}
                placeholder="Name or email"
              />
            </Field>
            <Field label="Payment">
              <select
                value={filters.payment}
                onChange={(event) => setFilter("payment", event.target.value)}
              >
                <option value="">All payment states</option>
                {["none", "unpaid", "part_paid", "paid", "overdue"].map((value) => (
                  <option key={value} value={value}>
                    {titleCase(value)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Needs attention">
              <select
                value={filters.attention}
                onChange={(event) => setFilter("attention", event.target.value)}
              >
                <option value="">Everything</option>
                {attentionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <form className="saved-view-controls" onSubmit={saveView}>
            <Field label="View name">
              <input
                value={viewName}
                onChange={(event) => setViewName(event.target.value)}
                maxLength={120}
                required
              />
            </Field>
            <Field label="Visibility">
              <select
                value={viewVisibility}
                onChange={(event) => setViewVisibility(event.target.value as "private" | "tenant")}
              >
                <option value="private">Only me</option>
                {canShareViews ? <option value="tenant">Everyone in this tenant</option> : null}
              </select>
            </Field>
            <label className="check-field">
              <input
                type="checkbox"
                checked={viewDefault}
                onChange={(event) => setViewDefault(event.target.checked)}
              />
              <span>Use as my default view</span>
            </label>
            <div className="page-actions">
              <button className="button secondary" type="button" onClick={resetFilters}>
                <RotateCcw size={16} /> Clear
              </button>
              {selectedView ? (
                <>
                  <button
                    className="button secondary"
                    type="button"
                    disabled={working || !canEditSelectedView}
                    onClick={updateView}
                  >
                    Update selected
                  </button>
                  <button
                    className="button danger"
                    type="button"
                    disabled={working || !canEditSelectedView}
                    onClick={deleteView}
                  >
                    Delete
                  </button>
                </>
              ) : null}
              <button
                className="button primary"
                type="submit"
                disabled={working || !viewName.trim()}
              >
                <Save size={16} /> Save new
              </button>
            </div>
          </form>
        </Card>
      ) : null}

      <CalendarViews
        mode={mode}
        anchor={anchor}
        days={days}
        items={items}
        noun={noun}
        createPath={createPath}
        canManageBookings={canManageBookings}
      />
    </div>
  );
}
